import React, { useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  makeStyles,
  MenuItem,
  Grid,
  Backdrop,
  CircularProgress,
  Typography,
} from "@material-ui/core";

import { useAlert } from "react-alert";

import { createExihibit } from "../../../adminModel/exihibition";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    width: "100%",
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  cardHeader: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: "#fafafa",
  },

  cardTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#222",
  },

  cardSubtitle: {
    marginTop: 4,
    fontSize: 12,
    color: "#888",
  },

  cardContent: {
    padding: theme.spacing(3),
  },

  section: {
    width: "100%",
  },

  sectionHeader: {
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid #eeeeee",
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  sectionDescription: {
    marginTop: 3,
    fontSize: 11,
    color: "#999",
  },

  field: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  descriptionField: {
    marginTop: theme.spacing(1.5),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
      alignItems: "flex-start",
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
      lineHeight: 1.6,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  actionWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(1.5, 3),
    backgroundColor: "#fafafa",
  },

  saveButton: {
    minWidth: 135,
    height: 40,
    borderRadius: 6,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

const Create = ({ className, ...rest }) => {
  const classes = useStyles();
  const alert = useAlert();

  const [loading, setLoading] = useState(false);

  const [values, setValues] = useState({
    auction_name: "",
    auction_slug: "",
    auction_descriptions: "",
    catalog_download: "",
    catalog_url: "",
    date_start: "",
    date_end: "",
    auctionType: "",
    folder_image: "",
    isShow: "",
    isLive: "",
    showHp: "",
    auction_status: "",
    exhibit_name: "",
    exhibit_desc: "",
  });

  // =========================
  // FORM CHANGE
  // =========================

  const handleChange = (e) => {
    const {
      name,
      value,
    } = e.target;

    setValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // =========================
  // SUBMIT
  // =========================

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    const arrayData = {
      exhibit_name: values.exhibit_name,
      exhibit_desc: values.exhibit_desc,
      date_start: values.date_start,
      date_end: values.date_end,
      auction_status: values.auction_status,
    };

    try {
      const result =
        await createExihibit(arrayData);

      setLoading(false);

      if (
        result ===
        "Auction Name already Exist"
      ) {
        alert.error(result);
      } else {
        alert.success(result);
      }
    } catch (e) {
      setLoading(false);

      console.log(e);

      alert.error(
        "Failed to create exhibition"
      );
    }
  };

  return (
    <div
      className={clsx(
        classes.root,
        className
      )}
      {...rest}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <form
            onSubmit={handleFormSubmit}
            autoComplete="off"
          >
            <Card
              className={classes.card}
            >
              {/* =========================
                  HEADER
              ========================= */}

              <CardHeader
                className={
                  classes.cardHeader
                }
                title={
                  <Typography
                    className={
                      classes.cardTitle
                    }
                  >
                    Create Exhibition
                  </Typography>
                }
                subheader={
                  <Typography
                    className={
                      classes.cardSubtitle
                    }
                  >
                    Enter the exhibition
                    information below
                  </Typography>
                }
              />

              <Divider />

              {/* =========================
                  FORM CONTENT
              ========================= */}

              <CardContent
                className={
                  classes.cardContent
                }
              >
                <div
                  className={
                    classes.section
                  }
                >
                  <div
                    className={
                      classes.sectionHeader
                    }
                  >
                    <Typography
                      className={
                        classes.sectionTitle
                      }
                    >
                      Exhibition Information
                    </Typography>

                    <Typography
                      className={
                        classes.sectionDescription
                      }
                    >
                      Provide the exhibition
                      name, description, dates,
                      and status.
                    </Typography>
                  </div>

                  <Grid
                    container
                    spacing={3}
                  >
                    {/* EXHIBITION NAME */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        required
                        label="Exhibition Name"
                        name="exhibit_name"
                        value={
                          values.exhibit_name
                        }
                        type="text"
                        variant="outlined"
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>

                    {/* DESCRIPTION */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.descriptionField
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Exhibition Description"
                        name="exhibit_desc"
                        value={
                          values.exhibit_desc
                        }
                        type="text"
                        variant="outlined"
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>

                    {/* START DATE */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        required
                        label="Start Date"
                        name="date_start"
                        type="date"
                        value={
                          values.date_start
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                      />
                    </Grid>

                    {/* END DATE */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        required
                        label="End Date"
                        name="date_end"
                        type="date"
                        value={
                          values.date_end
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                      />
                    </Grid>

                    {/* STATUS */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        required
                        select
                        name="auction_status"
                        label="Exhibition Status"
                        value={
                          values.auction_status
                        }
                        onChange={
                          handleChange
                        }
                        variant="outlined"
                      >
                        <MenuItem
                          value={0}
                        >
                          PREVIOUS AUCTION
                        </MenuItem>

                        <MenuItem
                          value={1}
                        >
                          UPCOMING AUCTION
                        </MenuItem>

                        <MenuItem
                          value={2}
                        >
                          ONGOING AUCTION
                        </MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </div>
              </CardContent>

              <Divider />

              {/* =========================
                  FOOTER
              ========================= */}

              <Box
                className={
                  classes.actionWrapper
                }
              >
                <Button
                  className={
                    classes.saveButton
                  }
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? "Saving..."
                    : "Save Details"}
                </Button>
              </Box>
            </Card>
          </form>
        </Grid>
      </Grid>

      {/* =========================
          LOADING
      ========================= */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress
          color="inherit"
        />
      </Backdrop>
    </div>
  );
};

Create.propTypes = {
  className: PropTypes.string,
};

export default Create;